import type { Tier } from "./marker-modes.js";

/** The tiers whose usage limits are locked. Free has no daily budget axis,
 * only calls x per-call chars; Ultra is deliberately absent — its shape is
 * still TBD (`docs/superpowers/specs/2026-06-30-pricing-lock.md`), so
 * nothing here should claim a number for it. */
export type LimitedTier = "free" | "docs" | "api" | "pro";

/**
 * Per-tier usage limits, in minor-unit-free plain numbers (calls, characters).
 * `null` means "uncapped on this axis" — for `callsPerDay` that is Pro's
 * actual unlimited-calls promise; for `dailyCharBudget` / `burstPerMin` on
 * Free/Docs/API it means the tier has no such axis at all (its capacity is
 * bounded by `callsPerDay x charsPerCall` instead — the pricing-lock spec's
 * "Calls x Per-Call" shape, vs. Pro's "Daily Budget + Per-Call" pool shape).
 */
export type TierLimits = {
  /** Max anonymize calls per rolling 24h. */
  callsPerDay: number | null;
  /** Max characters accepted in a single call. */
  charsPerCall: number;
  /** Max characters summed across all calls in a rolling 24h window. */
  dailyCharBudget: number | null;
  /** Max calls per rolling minute. */
  burstPerMin: number | null;
};

/**
 * What we publicly claim each tier's limits are — the single source of
 * truth for that claim. Locked 2026-06-30
 * (`docs/superpowers/specs/2026-06-30-pricing-lock.md`), superseding the
 * 2026-06-27 numbers that shipped in `apps/api/src/rate-limit/tier-limits.ts`
 * and were never brought forward.
 *
 * Four parties have to agree on these numbers and, until this table existed,
 * agreed only by hand:
 *
 * 1. the pricing page copy the customer reads (`pricing.tier.*` /
 *    `pricing.row.*` in the i18n files, plus the JSX literals in
 *    `pricing.tsx`) — checked against this table by
 *    `apps/web/src/i18n/pricing-limits.test.ts`;
 * 2. the api's rate-limit resolver (`apps/api/src/rate-limit/tier-limits.ts`),
 *    which reads its per-tier defaults straight from this table — checked by
 *    `apps/api/src/rate-limit/tier-limits.test.ts`;
 * 3. the web client's own mirrors (the redact form's live char counter, the
 *    account usage meter) via `clientCharLimitForTier` below;
 * 4. this table.
 *
 * A customer who reads "100,000 characters" on the pricing page and gets
 * bounced at 40,000 mid-integration is the failure this closes — same shape
 * as the pricing-vs-store drift `billing-prices.ts` closes for money instead
 * of limits.
 *
 * Docs and API deliberately have different shapes (large-file-few-calls vs.
 * small-file-many-calls) despite sharing a price — see the pricing-lock spec
 * for the persona reasoning. Pro is the one tier with a `dailyCharBudget`:
 * unlimited calls/day, bounded by chars/day and chars/call instead.
 */
export const TIER_LIMITS: Record<LimitedTier, TierLimits> = {
  free: { callsPerDay: 20, charsPerCall: 2_500, dailyCharBudget: null, burstPerMin: null },
  docs: { callsPerDay: 100, charsPerCall: 40_000, dailyCharBudget: null, burstPerMin: null },
  api: { callsPerDay: 500, charsPerCall: 8_000, dailyCharBudget: null, burstPerMin: null },
  pro: {
    callsPerDay: null,
    charsPerCall: 200_000,
    dailyCharBudget: 10_000_000,
    burstPerMin: 120,
  },
};

/**
 * Formats a whole-number-of-millions char budget the way the pricing copy
 * writes it ("10 Mio" / "10M") instead of the spelled-out digit string
 * ("10.000.000" doesn't read as a limit on a pricing card). Bound to
 * `TIER_LIMITS.pro.dailyCharBudget` by `pricing-limits.test.ts` — the
 * computed string, not a hand-written one, is what makes bending the number
 * in the table above also bend the value this asserts against, so a drift
 * between the two can't pass silently.
 */
export function formatDailyBudgetMillions(chars: number, locale: "de" | "en"): string {
  if (!Number.isInteger(chars) || chars % 1_000_000 !== 0) {
    throw new Error(`formatDailyBudgetMillions: ${chars} is not a whole number of millions`);
  }
  const millions = chars / 1_000_000;
  return locale === "de" ? `${millions} Mio` : `${millions}M`;
}

/**
 * Docx upload byte cap (spec 2026-06-16): 10 MB, flat across every tier
 * that can upload a file — file access is gated by tier (Docs and up), but
 * the cap itself doesn't vary by tier the way `TIER_LIMITS` does. Single
 * source for the upload form's client-side pre-check
 * (`apps/web/src/components/redact/file-form.tsx`) and `@unpii/config`'s
 * `files.maxBytes` default, which imports this directly. Was three
 * independent hardcodes before `night/file-max-bytes-single-source`
 * (2026-08-23). The error view's own no-limit-echoed fallback
 * (`file-error-view.tsx`) reads `FILE_UPLOAD_MAX_BYTES` below instead, since
 * PDF landed with its own, larger cap.
 *
 * A decimal MB, not a binary MiB (`ux/decimal-mb`, 2026-08-26):
 * the displayed cap and the enforced cap must agree in the same unit
 * shoppers actually read it in, or "10 MB" on the page silently means
 * "10.49 MB" at the wall. That makes this cap marginally *smaller* than
 * before (10_485_760 → 10_000_000 bytes) — never a silent limit raise.
 */
export const DOCX_MAX_BYTES = 10 * 1_000_000;

/**
 * Per-request character limit by tier. Client-side mirror of the server-side
 * `charLimitForTier` in `apps/api/src/rate-limit/tier-limits.ts`. Kept in
 * shared/ so both the redact form and the account-usage page show identical
 * numbers without one of them drifting. Derived from TIER_LIMITS above for
 * every tier that has a locked number; anon shares Free's cap (same
 * anonymous-vs-signed-out-free story as the server side), and Ultra has no
 * locked number yet so it stays the historical "no cap" placeholder.
 *
 * Admin bypass is server-only; this helper returns the documented tier limit.
 */
export function clientCharLimitForTier(tier: Tier): number {
  switch (tier) {
    case "anon":
    case "free":
      return TIER_LIMITS.free.charsPerCall;
    case "docs":
      return TIER_LIMITS.docs.charsPerCall;
    case "api":
      return TIER_LIMITS.api.charsPerCall;
    case "pro":
      return TIER_LIMITS.pro.charsPerCall;
    case "ultra":
      return Number.MAX_SAFE_INTEGER;
  }
}

/**
 * Per-tier quota for user-managed pattern lists — custom redaction rules and
 * allowlist entries. The two features share one quota shape and one set of
 * numbers by product decision, not by coincidence (Phase 4 tier reshape,
 * 2026-06-27): every paid tier (Docs / API / Pro / Ultra) gets a quota; Pro
 * (combo) inherits the old pre-Phase-4 single-proplus slot of 100.
 * Free/anon are listed explicitly with 0 — the routes' requireDocsGuard
 * rejects those callers with 402 before a quota check runs, so the 0 is
 * effectively unreachable, but keeping it as a real key (rather than
 * relying on a `?? 0` fallback) keeps every consumer total over `Tier`.
 *
 * Not part of the Phase 5 pricing-lock reshape (calls/chars only) — kept as
 * its own table rather than folded into TIER_LIMITS above.
 *
 * Single source of truth for:
 * - apps/api/src/rate-limit/tier-limits.ts's maxRulesForTier()
 * - apps/api/src/routes/allowlist.ts's per-request quota check
 * - apps/web/src/components/account/custom-rules.tsx's client-side badge
 * - apps/web/src/components/account/allowlist.tsx's client-side badge
 *
 * Previously hand-copied across all four with a "symmetric to" comment
 * pointing at the others instead of a shared import.
 */
export const PATTERN_LIST_QUOTA: Record<Tier, number> = {
  anon: 0,
  free: 0,
  docs: 20,
  api: 20,
  pro: 100,
  ultra: Number.MAX_SAFE_INTEGER,
};

/**
 * Shortest custom-rule pattern and allowlist value a WRITE accepts (owner
 * decision 2026-09-16). Lives next to PATTERN_LIST_QUOTA rather than in
 * schemas.ts because apps/api/src/lib/validate-regex.ts needs it too, and that
 * file is its own worker-thread entry point: it imports nothing but
 * node:worker_threads so a canary spawn stays cheap, and this module has no
 * runtime imports at all. Enforced on create, update, and import - never on the
 * read schemas (`CustomRedactionRule`, `AllowlistEntry`), so a row saved
 * before the bound existed still parses in every response and stays usable
 * until someone edits it.
 *
 * Two, not three - and the reason is the honorific trim (#359). Shipped as 3
 * the same day and lowered after the two branches met: once a PERSON span no
 * longer carries its salutation, "Frau Li" arrives as the span "Li", the
 * "Erlauben" button prefills exactly that, and a bound of 3 made every
 * two-letter surname (Li, Wu, Xu, Ng, Ma) impossible to allow. The case 3
 * guarded against has also shrunk: an allowlist entry is a full match since
 * the same day (shared/allowlist.ts), so a short one opens only a span that is
 * exactly that short text, not every span it occurs in.
 *
 * What remains is the same guard for two different jobs:
 *  - Allowlist: a one-character value is far more likely a slip than an
 *    intent, and the slip fails in the plaintext direction.
 *  - Custom rules: a typo guard and nothing more. A regex's length says
 *    nothing about what it matches (`[a-z]` is five characters and matches
 *    one), and an over-broad rule over-redacts, which Charter §4 calls the
 *    safe side. It stops an accidental rule on `a`, and is symmetric on
 *    purpose so both dialogs behave alike.
 */
export const PATTERN_LIST_MIN_LENGTH = 2;

/**
 * PDF upload byte cap: 20 MB, flat across every tier that can upload a file
 * — same tier-gating-but-not-tier-varying story as `DOCX_MAX_BYTES` above.
 * Deliberately larger than the docx cap: a scanned-looking but text-bearing
 * PDF carries embedded fonts and images that a .docx of the same prose does
 * not, so the docx number would reject ordinary business documents.
 * Enforced AFTER the format sniff in `POST /api/v1/anonymize-file`, so the
 * 413 body can name the limit that actually applies to the uploaded format.
 *
 * Decimal MB, not binary MiB — same `ux/decimal-mb` (2026-08-26)
 * reasoning as `DOCX_MAX_BYTES`: 20_971_520 → 20_000_000 bytes, so the
 * displayed cap is the enforced one, and only ever smaller than before.
 */
export const PDF_MAX_BYTES = 20 * 1_000_000;

/**
 * Page cap for a single PDF upload. Bounds worst-case extraction work per
 * request independently of `PDF_MAX_BYTES` — a 2 MB PDF can still hold
 * thousands of pages, and page count, not byte count, is what pdf.js
 * parsing time scales with. Checked before the first page is extracted, so
 * an over-long document costs a page-tree read rather than a full parse.
 */
export const PDF_MAX_PAGES = 500;

/**
 * Multipart-level byte cap for the file-upload route: the largest per-format
 * cap, since the format is not known until the first bytes have been read.
 * The per-format cap (`DOCX_MAX_BYTES` / `PDF_MAX_BYTES`) is the one that
 * decides the request — this only bounds what the multipart layer will buffer
 * before any sniff can happen.
 */
export const FILE_UPLOAD_MAX_BYTES = Math.max(DOCX_MAX_BYTES, PDF_MAX_BYTES);
